import * as ko from "knockout";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as AuthenticationData from "../data/authenticationData";
import { IApplication } from "../interfaces/application";
import * as Data from "../interfaces/data";

interface ILoginControlViewModel {
    UserName: KnockoutObservable<string>;
    Password: KnockoutObservable<string>;
    UserNameMissing: KnockoutObservable<boolean>;
    PasswordMissing: KnockoutObservable<boolean>;
    LoginInvalid: KnockoutObservable<boolean>;
    AccountLocked: KnockoutObservable<boolean>;
    AccountLockedLength: KnockoutObservable<number>;
    LoginHelp: KnockoutObservable<string>;
    CookiesEnabled: KnockoutObservable<boolean>;
    selfRegistrationAllowed: boolean;
    onSelfRegistration: () => void;
    onLogin: () => void;
    onForgotPassword: () => void;
    viewTOC: () => void;
    hideTOC: () => void;
}

export class LoginControl {
    private container: JQuery;
    private tocModal: JQuery;
    private viewModel: ILoginControlViewModel;

    constructor() {
        const me: LoginControl = this;
        const app: IApplication = Global.app();

        this.viewModel = {
            UserName: ko.observable(""),
            Password: ko.observable(""),
            UserNameMissing: ko.observable(false),
            PasswordMissing: ko.observable(false),
            LoginInvalid: ko.observable(false),
            AccountLocked: ko.observable(false),
            AccountLockedLength: ko.observable(0),
            LoginHelp: ko.observable(""),
            CookiesEnabled: ko.observable(this.checkCookiesEnabled()),
            selfRegistrationAllowed: app.global.SelfRegistrationAllowed,
            onSelfRegistration(): void {
                app.router().navigate("/SelfRegistration", true);
            },
            onLogin(): void {
                me.processLoginRequest();
            },
            onForgotPassword(): void {
                app.router().navigate("/ForgotPassword", true);
            },
            viewTOC(): void {
                me.tocModal.modal("show");
            },
            hideTOC(): void {
                me.tocModal.modal("hide");
            },
        };
    }

    public render(container: JQuery): JQuery.Promise<any> {
        const me: LoginControl = this;
        const app: IApplication = Global.app();
        const deferred: JQuery.Deferred<any> = $.Deferred();

        this.container = container;

        app.templates().render("logincontrol", "template_logincontrol", this.container)
            .done(() => {
                this.tocModal = container.find("#tocModal");

                ko.applyBindings(this.viewModel, container[0]);

                // process if user presses enter while in a login field
                this.container.find("input[data-id='loginField']").keypress((e: any) => {
                    if (e.which === 13) {
                        $(e.target).blur();
                        me.processLoginRequest();
                    }
                });

                this.setFocusToUserNameField();

                deferred.resolve();
            });

        return deferred.promise();
    }

    private checkCookiesEnabled(): boolean {
        if (Global.app().webServiceBaseDomain === "localhost") {
            return true;
        }

        // https://github.com/Modernizr/Modernizr/blob/master/feature-detects/cookies.js
        try {
            // The "SameSite" property is being set to "None" to support hosting
            // OnCue in an iframe, like Advantage VSS.
            document.cookie = "cookietest=1;";
            const ret = document.cookie.indexOf("cookietest=") !== -1;
            document.cookie = "cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT; SameSite=None; Secure;";
            return ret;
        } catch (e) {
            return false;
        }
    }

    private processLoginRequest(): void {
        const me: LoginControl = this;
        const app: IApplication = Global.app();
        let waitContainer: JQuery<HTMLElement>;
        let targetContainer: JQuery<HTMLElement>;
        let request: AuthenticationData.IAuthenticateUserRequest;

        if (!this.validateLogin()) {
            me.setFocusToUserNameField();
            return;
        }

        targetContainer = this.container.find(".login_panel_body");
        waitContainer = UI.showWaitIndicator(targetContainer);

        request = {
            UserName: this.viewModel.UserName(),
            Password: this.viewModel.Password(),
        };

        AuthenticationData.AuthenticateUser(request)
            .done((response: Data.IResponse<AuthenticationData.IAuthenticationResponse>) => {
                if (response.Success) {
                    if (response.Payload.IsAuthenticated) {
                        // user logged in OK
                        me.viewModel.LoginInvalid(false);
                        me.viewModel.AccountLocked(false);
                        // save the user information for use by all other pages
                        app.loginUser(response.Payload.AuthenticationToken, response.Payload.UserSession, response.Payload.ReportToken);
                        // if url was passed in redirect to that url instead of default page for user
                        if (app.pageManager().redirectURL) {
                            window.location.href = app.pageManager().redirectURL;
                        } else {
                            // goto the correct home page for the user
                            app.pageManager().closeAllAndGotoDefaultPage();
                        }
                    } else {
                        // could not log, check the reason and take action

                        if (response.Payload.MustChangePassword) {
                            // user name/password was OK, but user must change their password
                            app.router().navigate("/ResetPassword/Expired/" + response.Payload.PasswordResetToken, true);
                        } else if (response.Payload.ExceededMaximumAttempts) {
                            // login has failed maximum number of times, account is now locked
                            me.viewModel.LoginInvalid(false);
                            me.viewModel.AccountLocked(true);
                            me.viewModel.AccountLockedLength(response.Payload.AccountLockedLength);
                        } else {
                            // for all other reasons, let the user retry
                            me.setFocusToUserNameField();
                            me.viewModel.LoginInvalid(true);
                            me.viewModel.AccountLocked(false);
                        }
                    }
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(targetContainer, waitContainer);
            });

    }

    private validateLogin(): boolean {
        let isValid: boolean = true;

        isValid = UI.setRequiredStringError(this.viewModel.UserName(), "UserNameMissing", this.viewModel) ? isValid : false;
        isValid = UI.setRequiredStringError(this.viewModel.Password(), "PasswordMissing", this.viewModel) ? isValid : false;

        return isValid;
    }

    private setFocusToUserNameField(): void {
        this.container.find("input[data-id='loginField']").first().focus();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/login.ts